/**
 * Session-cache freshness for pedalPricing docs (I-4 consumer-path review
 * finding). The module-level caches in useReverbPricing and
 * usePedalMarketValues previously held a doc for the WHOLE SPA session —
 * fine when pricing changed at most weekly, but transaction evidence now
 * changes docs at event time, and a reversal must not keep showing a price
 * for hours because an offer grid cached the doc at app start. Entries
 * therefore expire after TTL_MS and the next consumer refetches.
 *
 * This bounds staleness; it does not make updates instant. The honest
 * update-latency statement lives in REVERB_PRICING_REVIEW_PACKAGE.md §36:
 * server recompute is seconds (triggers), and a client sees it on its next
 * fetch — page view, cache expiry for the batched hooks, or the BOUNDED
 * return-to-view refresh added below.
 *
 * BOUNDED REFRESH (owner instruction, 2026-10-12): "do not add polling to
 * every Browse card. Implement a bounded refresh on pricing-screen
 * re-entry or return to visibility, deduplicated by pricing identity, and
 * expiry re-evaluation for mounted offer grids."
 *
 * So this module owns three things, and deliberately nothing more:
 *   1. THE ONE staleness rule — PRICING_DOC_CACHE_TTL_MS, read through
 *      freshCachedDoc (lookup path) and isPastTtl (refresh gate). There is
 *      no second freshness rule anywhere; a refresh that is inside the TTL
 *      costs nothing, which is what keeps the common case free.
 *   2. The pricing-identity keyed doc store shared by the callable path
 *      (useReverbPricing's lookups and PriceIndicator's own fetch) plus an
 *      IN-FLIGHT MAP on the same key, so N mounted components on one
 *      identity cause at most ONE network call. PedalDetails renders
 *      PriceIndicator twice (one of the two is hidden by a media query), so
 *      this is the difference between one and two callable invocations per
 *      listing view.
 *   3. One shared return-to-view signal (visibilitychange, plus the native
 *      app resume when running under Capacitor) that interested screens
 *      subscribe to. It is an EVENT, never a poll: nothing here is on an
 *      interval.
 *
 * NOT here, on purpose: usePedalMarketValues keeps its own store. It reads
 * pedalPricing/{key} straight from Firestore on a different key space
 * (buildPedalPricingKey), and the owner's instruction for those grids is
 * expiry re-evaluation, not refetching. Merging the two stores would start
 * coupling grid reads to callable fetches, which nobody asked for.
 */
import { Capacitor } from '@capacitor/core';

export const PRICING_DOC_CACHE_TTL_MS = 10 * 60 * 1000;

/** The cached doc when the entry is present and fresh, else undefined
 * (undefined means "fetch again"; a cached null MISS is a valid value). */
export function freshCachedDoc(entry, nowMs = Date.now()) {
  if (!entry || entry.data === undefined) return undefined;
  if (!Number.isFinite(entry.at) || nowMs - entry.at > PRICING_DOC_CACHE_TTL_MS) return undefined;
  return entry.data;
}

/**
 * The doc an entry holds REGARDLESS of age, or null.
 *
 * Used only for RE-EVALUATION of something already on screen: a mounted
 * grid re-running the evaluator must judge the document it is actually
 * displaying, not treat it as absent because the TTL lapsed. Blanking a
 * displayed value on a timer, with no fetch to replace it, would be worse
 * than showing it — the expiry that genuinely must withdraw a band is the
 * server's staleAfterMs stamp, which the evaluator enforces itself.
 * Lookups keep using freshCachedDoc.
 */
export function heldDoc(entry) {
  if (!entry || entry.data === undefined) return null;
  return entry.data;
}

/**
 * Is a fetch stamp older than the TTL? Same constant, same rule as
 * freshCachedDoc — this is the gate for the return-to-view refresh, which
 * must do nothing at all while the held doc is still fresh.
 * A missing/garbage stamp counts as stale (there is nothing to trust).
 */
export function isPastTtl(atMs, nowMs = Date.now()) {
  if (!Number.isFinite(atMs)) return true;
  return nowMs - atMs > PRICING_DOC_CACHE_TTL_MS;
}

/**
 * The pricing identity a fetch is keyed by: make + model + decade, with the
 * decade ALREADY normalized by the caller (normalizeDecade) so the key
 * matches what the server will be asked for. Identical string to the key
 * useReverbPricing has always used, so the existing cache semantics are
 * unchanged; it lives here now because PriceIndicator has to key the same
 * way for dedupe to work.
 */
export function pricingIdentityKey(make, model, normDecade) {
  return `${make}|${model}|${normDecade}`;
}

// ── The shared doc store (callable path) ───────────────────────────────────
// Entries: { data, at }. `data` may be null — a cached MISS is a real value.
const pricingDocs = new Map();

export function getPricingEntry(key) {
  return pricingDocs.get(key);
}

export function setPricingEntry(key, data, nowMs = Date.now()) {
  pricingDocs.set(key, { data, at: nowMs });
}

// ── Dedupe by pricing identity ─────────────────────────────────────────────
// One in-flight promise per identity. Cleared when it settles, so this is a
// coalescer for CONCURRENT demand and never a second cache with its own
// lifetime.
const inFlight = new Map();

/**
 * Run `fetcher()` for this pricing identity unless a fetch for the same
 * identity is already in flight, in which case join it. Every caller gets
 * the same resolved value.
 */
export function dedupePricingFetch(key, fetcher) {
  const existing = inFlight.get(key);
  if (existing) return existing;
  let started;
  try {
    started = Promise.resolve(fetcher());
  } catch (err) {
    return Promise.reject(err);
  }
  const tracked = started.then(
    (value) => { inFlight.delete(key); return value; },
    (err) => { inFlight.delete(key); throw err; }
  );
  inFlight.set(key, tracked);
  return tracked;
}

/** Test/diagnostic introspection: how many identities are mid-fetch. */
export function pricingFetchesInFlight() {
  return inFlight.size;
}

// ── The shared return-to-view signal ──────────────────────────────────────
// ONE pair of listeners for the whole app, fanned out to subscribers. Two
// mounted PriceIndicators must not mean two visibilitychange handlers each
// deciding independently to fetch; they decide against the same shared TTL
// stamp and collapse through the in-flight map above.
//
// Native: iOS suspends the WebView on background, and the app resume event
// is the signal the shell already trusts for exactly this class of problem
// (src/App.js cycles Firestore's transport on CapacitorApp 'resume' because
// visibility alone was not enough to prove the webview was live). We reuse
// that SAME plugin — @capacitor/app, already a dependency and already used
// by App.js and usePushDeviceStatus — rather than adding anything native.
// The import is dynamic and gated on isNativePlatform(), so web builds and
// the jsdom test environment never load the plugin at all.
const returnToViewSubscribers = new Set();
let domListenerAttached = false;
let nativeResumeHandle = null;
let nativeResumeToken = 0;

function notifyReturnToView() {
  // Copy first: a subscriber may unsubscribe from inside its own callback.
  for (const subscriber of [...returnToViewSubscribers]) {
    try {
      subscriber();
    } catch (err) {
      // A broken subscriber must not stop the others, and must never
      // surface on a price display.
    }
  }
}

function handleVisibilityChange() {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'visible') notifyReturnToView();
}

function attachReturnToViewListeners() {
  if (domListenerAttached || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  domListenerAttached = true;
  if (!Capacitor.isNativePlatform()) return;
  const token = ++nativeResumeToken;
  import('@capacitor/app')
    .then(({ App }) => App.addListener('resume', notifyReturnToView))
    .then((handle) => {
      // Detached again before the plugin finished loading: drop it.
      if (token !== nativeResumeToken) {
        handle.remove();
        return;
      }
      nativeResumeHandle = handle;
    })
    .catch(() => { /* no resume signal; visibilitychange still applies */ });
}

function detachReturnToViewListeners() {
  if (!domListenerAttached) return;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  domListenerAttached = false;
  nativeResumeToken += 1;
  if (nativeResumeHandle) {
    nativeResumeHandle.remove();
    nativeResumeHandle = null;
  }
}

/**
 * Call `listener` when the app comes back into view (tab becomes visible,
 * or the native app resumes). Returns an unsubscribe function. The
 * listener is responsible for its own TTL gate — this signal says only
 * "the user is looking again".
 */
export function subscribeReturnToView(listener) {
  returnToViewSubscribers.add(listener);
  attachReturnToViewListeners();
  return () => {
    returnToViewSubscribers.delete(listener);
    if (returnToViewSubscribers.size === 0) detachReturnToViewListeners();
  };
}

/**
 * Does a stored pricing document prove it belongs to this make/model?
 *
 * MIRROR of functions/fetchReverbPricing.js#isStoredIdentityVerified, using
 * the same strict rule as the server matcher: formatting folds (case,
 * separators, trademark glyphs), meaningful characters do not ('+' keeps
 * Trio+ apart from Trio). A document with no recorded make/model cannot be
 * verified and is rejected.
 *
 * WHY IT LIVES HERE: every read path needs it — the logged-out
 * useReverbPricing fetch AND the usePedalMarketValues offer-grid fetch —
 * and putting it in one of those hooks meant the other had to import a hook
 * from a hook. Both already import this module.
 *
 * Fail-closed costs nothing for real documents: both the v1 writer
 * (verified at ed777c3) and the v2 writer store make and model on every
 * pedalPricing document. Only genuinely ambiguous or mismatched documents
 * are refused.
 */
export function storedDocMatchesIdentity(docData, make, model) {
  if (!docData || typeof docData.make !== 'string' || typeof docData.model !== 'string') {
    return false;
  }
  const fold = (x) => String(x || '').toLowerCase()
    .replace(/[\u00ae\u2122\u00a9]/g, '').replace(/[\s\-_/.,&]+/g, '').trim();
  const a = fold(docData.make); const b = fold(make);
  const c = fold(docData.model); const d = fold(model);
  return a.length > 0 && c.length > 0 && a === b && c === d;
}

/**
 * A cached pricing document, but ONLY if it proves it belongs to this
 * make/model. Returns `undefined` — i.e. "treat as a miss, go and fetch" —
 * both when there is no fresh entry AND when the entry fails verification.
 *
 * WHY A MISS RATHER THAN A NULL (review point 2): verifying only freshly
 * FETCHED documents was insufficient, because an entry already in the cache
 * (put there by an earlier render, or by a build that predates
 * verification) would be served unchecked. Returning a miss lets the caller
 * refetch and overwrite it; returning null would pin it as "no data".
 *
 * WHAT HAPPENS AFTER A REFUSAL DEPENDS ON THE CALLER, so do not state it
 * as universal behaviour:
 *   - usePedalMarketValues (offer grids) CACHES the refusal as an ordinary
 *     miss (data === null). From then on this function returns that null as
 *     a legitimate hit, so no further fetch happens until the 10-minute
 *     entry lapses. Net behaviour there: fail-closed, with a TTL-BOUNDED
 *     BLANK WINDOW rather than instant recovery. Caching it is deliberate —
 *     the alternative is a Firestore read on every render for a mismatched
 *     key.
 *   - useReverbPricing caches ONLY valid documents (`if (data)
 *     setPricingEntry(key, data)`), so a refusal leaves no entry and the
 *     next lookup fetches again. No blank window, at the cost of a repeated
 *     fetch while the mismatch persists.
 * Both are fail-closed; they differ only in how the retry is paced.
 *
 * A cached MISS (data === null) passes through untouched either way: there
 * is no document to misattribute, and treating it as a miss would turn the
 * grid's deliberate pacing into a read loop.
 */
export function verifiedFreshDoc(entry, make, model) {
  const data = freshCachedDoc(entry);
  if (data === undefined || data === null) return data;
  return storedDocMatchesIdentity(data, make, model) ? data : undefined;
}
